var _excluded = ["children", "asChild"];
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { forwardRef as reactForwardRef } from 'react';
import * as DialogPrimitive from '@grupoboticario/flora-react-dialog';
import { forwardRef, keyframes, styled } from '@grupoboticario/flora-react-system';
/**
 * @component
 * @name Drawer
 * @description Drawer component
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export var Drawer = /*#__PURE__*/reactForwardRef((props, ref) => {
  return /*#__PURE__*/React.createElement(DialogPrimitive.Dialog, _extends({}, props, {
    "data-flora": "drawer",
    ref: ref
  }));
});
var slideIn = keyframes({
  from: {
    transform: '$$transformValue',
    opacity: 0
  },
  to: {
    transform: 'translate3d(0,0,0)',
    opacity: 1
  }
});
var slideOut = keyframes({
  from: {
    transform: 'translate3d(0,0,0)',
    opacity: 1
  },
  to: {
    transform: '$$transformValue',
    opacity: 0
  }
});
var StyledContent = styled(DialogPrimitive.DialogContent, {
  backgroundColor: '$backgroundPrimary',
  boxShadow: '$colors$shadowLight 0 0 38px -10px, $colors$shadowDark 0 0 35px -15px',
  position: 'fixed',
  top: 0,
  bottom: 0,
  width: '100%',
  willChange: 'transform',
  display: 'flex',
  flexDirection: 'column',
  zIndex: '$modal',
  overflowX: 'auto',
  animationDuration: '.3s',
  animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
  '@media (prefers-reduced-motion: reduce)': {
    animationDuration: '1.5s',
    animationTimingFunction: 'linear'
  },
  '&[data-state="open"]': {
    animationName: slideIn
  },
  '&[data-state="closed"]': {
    animationName: slideOut
  },
  variants: {
    /**
     * Define qual o lado o drawer será exibido.
     */
    side: {
      top: {
        $$transformValue: 'translate3d(0,-100%,0)',
        width: '100%',
        height: 'auto',
        minHeight: 60,
        left: 0,
        '@mobile': {
          bottom: 'auto'
        }
      },
      right: {
        $$transformValue: 'translate3d(100%,0,0)',
        right: 0,
        '@tablet': {
          width: 380
        }
      },
      bottom: {
        $$transformValue: 'translate3d(0,100%,0)',
        width: '100%',
        height: 'auto',
        minHeight: 60,
        bottom: 0,
        left: 0,
        '@mobile': {
          top: 'auto'
        }
      },
      left: {
        $$transformValue: 'translate3d(-100%,0,0)',
        left: 0,
        '@tablet': {
          width: 380
        }
      }
    },
    /**
     * Define se o drawer deve ocupar toda a tela.
     */
    isFull: {
      true: {
        width: '100%',
        height: '100%'
      }
    }
  },
  defaultVariants: {
    side: 'right'
  }
});
var StyledBody = styled(DialogPrimitive.DialogBody, {});
var StyledHeader = styled(DialogPrimitive.DialogHeader, {});
var StyledFooter = styled(DialogPrimitive.DialogFooter, {});
var StyledTrigger = styled(DialogPrimitive.DialogTrigger, {});
var StyledClose = styled(DialogPrimitive.DialogClose, {});
/**
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export var DrawerContent = forwardRef((props, forwardedRef) => {
  return /*#__PURE__*/React.createElement(StyledContent, _extends({}, props, {
    "data-flora": "drawer-content",
    ref: forwardedRef
  }));
});
/**
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export var DrawerBody = forwardRef((props, forwardedRef) => {
  return /*#__PURE__*/React.createElement(StyledBody, _extends({}, props, {
    "data-flora": "drawer-body",
    ref: forwardedRef
  }));
});
/**
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export var DrawerHeader = forwardRef((_ref, forwardedRef) => {
  var {
      children,
      asChild
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/React.createElement(StyledHeader, _extends({}, props, {
    "data-flora": "drawer-header",
    ref: forwardedRef
  }), /*#__PURE__*/React.createElement(DialogPrimitive.DialogTitle, {
    asChild: asChild
  }, children));
});
/**
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export var DrawerFooter = forwardRef((props, forwardedRef) => {
  return /*#__PURE__*/React.createElement(StyledFooter, _extends({}, props, {
    "data-flora": "drawer-footer",
    ref: forwardedRef
  }));
});
/**
 * Componente que renderiza o botão (ou outro componente) que abre o drawer.
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export var DrawerTrigger = forwardRef((props, forwardedRef) => {
  return /*#__PURE__*/React.createElement(StyledTrigger, _extends({}, props, {
    asChild: true,
    "data-flora": "drawer-trigger",
    ref: forwardedRef,
    "aria-expanded": undefined
  }));
});
/**
 * ### **Importante:**
 *
 * Este componente quando usado **SEM** a prop `asChild`, deve ser o primeiro elemento dentro do `DrawerContent`.
 * Isso garante a correta leitura do leitor de tela e a funcionalidade de fechamento do drawer.
 *
 *
 * Componente que renderiza o botão (ou outro componente) que fecha o drawer.
 * Caso a propriedade `asChild` seja `true`, o componente renderiza o elemento filho como o botão de fechar.
 * Caso contrário, renderiza o ícone <CrossIcon /> como o botão de fechar.
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export var DrawerCloseButton = forwardRef((props, forwardedRef) => {
  return /*#__PURE__*/React.createElement(StyledClose, _extends({}, props, {
    "data-flora": "drawer-close",
    ref: forwardedRef
  }));
});
//# sourceMappingURL=Drawer.js.map